<script setup lang="ts">
import { min, regex, required } from '@vee-validate/rules';
import { storeToRefs } from 'pinia';
import { Field, FormContext, useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { ExpandableAddressSuggestion } from '@/api/address/autocomplete';
import {
  AvsIncomplete,
  AvsInteractionRequired,
  AvsMultiple,
  AvsNone,
  AvsVerified,
  verifyAddress,
} from '@/api/address/verification';
import AddressSuggestions from '@/components/base/AddressSuggestions.vue';
import DefaultAddressPopper from '@/components/base/DefaultAddressPopper.vue';
import Checkbox from '@/components/base/form/Checkbox.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import FormInput, { InputAttributes } from '@/components/base/form/FormInput.vue';
import Select from '@/components/base/form/OutlinedSelect.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import AvsModal from '@/components/checkout/steps/address/AvsModal.vue';
import { useCheckout } from '@/composables/useCheckout';
import {
  FlexibleMessageValidator,
  useAddressFields,
  useForm as formUtils,
} from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { useInitialRequest } from '@/stores/initialRequest';
import { NutsAddress } from '@/utils/address';

interface AddressValidations {
  city: FlexibleMessageValidator<string>;
  country: string;
  name: FlexibleMessageValidator<string>;
  phone: FlexibleMessageValidator<string>;
  postalCode?: FlexibleMessageValidator<string>;
  state?: FlexibleMessageValidator<string>;
  street1: FlexibleMessageValidator<string>;
}

const props = withDefaults(
  defineProps<{
    hideB2BCheckbox?: boolean;
    hideCompanyField?: boolean;
    hidePopper?: boolean;
    includeFormControls?: boolean;
    isButtonLoading?: boolean;
    parentForm?: FormContext;
    showAddressForm?: boolean;
    showMakeDefault?: boolean;
    stepNumber?: number;
    uniqueIdSuffix: string;
  }>(),
  {
    includeFormControls: true,
    showAddressForm: true,
    stepNumber: 2,
  },
);

const emit = defineEmits<{
  'address-submitted': [NutsAddress];
  'cancelled-address-suggestions': [];
  'selected-address-suggestion': [];
  tooltip: [string];
}>();

const address = defineModel<NutsAddress>({ required: true });

const store = useStore();

const { geoIp } = storeToRefs(useInitialRequest());

const {
  addressFieldVariationsForCountry,
  addressSuggestions,
  countryCodes,
  declineAddressSuggestions,
  lookupAddress,
  selectAddressSuggestion,
  showSuggestions,
  statesOrProvinces,
  validationRulesForCountry,
} = useAddressFields(address);
const { isB2B } = useCheckout(store);

const { addresses, contact, hasBusinessAccount } = storeToRefs(useCustomer());

const showB2BCheckbox = computed(
  () =>
    props.showAddressForm &&
    !props.hideB2BCheckbox &&
    !hasBusinessAccount.value &&
    // note: we can't use `isB2B` from useCheckout here because it would hide the checkbox when it's checked
    !contact.value?.isB2b,
);

const { handleSubmit, meta: formMeta } = props.parentForm ?? useForm<NutsAddress>();

const address1ElementFocused = ref(false);

const emitAddressSubmitted = (value: NutsAddress) => {
  emit('address-submitted', { ...value, signedTrustedMatchkey: undefined });
};

const searchAddress = (addressFragment: string) => {
  if (document.activeElement?.id === `streetName-${props.uniqueIdSuffix}`) {
    let coordinates: string | undefined;
    if (geoIp.value?.latitude && geoIp.value?.longitude) {
      coordinates = `${geoIp.value.latitude}, ${geoIp.value.longitude}`;
    }
    lookupAddress(addressFragment, coordinates);
  }
};

watch(
  () => address.value.street1,
  () => {
    if (address.value.street1) {
      searchAddress(address.value.street1);
    }
  },
);

const onBlurAddress1Input = () => {
  setTimeout(() => {
    address1ElementFocused.value = false;
    declineAddressSuggestions();
  }, 200);
};

const avsStatus = ref<
  AvsIncomplete | AvsInteractionRequired | AvsMultiple | AvsNone | AvsVerified
>();
const displayAddressVerificationModal = ref(false);

const submitAddress = handleSubmit(async () => {
  if (!props.showAddressForm) {
    emitAddressSubmitted(address.value);
    return;
  }

  avsStatus.value = await verifyAddress(address.value);
  if (avsStatus.value.status === 'verified') {
    emitAddressSubmitted(address.value);
  } else {
    displayAddressVerificationModal.value = true;
  }
});

const useSuggestedAddress = () => {
  const suggestedAddress = {
    ...(address.value as NutsAddress),
    ...(avsStatus.value as any)?.address,
  };
  emitAddressSubmitted(suggestedAddress);
  displayAddressVerificationModal.value = false;
};

const useEnteredAddress = () => {
  emitAddressSubmitted(address.value);
  displayAddressVerificationModal.value = false;
};

const confirmUnitNumber = async (unitInfo: string, updatedPostalCode?: string) => {
  const addressWithUnitNumber = {
    ...(address.value as NutsAddress),
    street2: `${address.value?.street2} ${unitInfo}`,
  };
  if (updatedPostalCode) {
    addressWithUnitNumber.postalCode = updatedPostalCode;
  }
  emitAddressSubmitted(addressWithUnitNumber);
  displayAddressVerificationModal.value = false;
};

const confirmHouseNumber = async (houseInfo: string) => {
  const addressWithHouseNumber = {
    ...(address.value as NutsAddress),
    street1: `${houseInfo} ${address.value?.street1}`,
  };
  displayAddressVerificationModal.value = false;
  avsStatus.value = await verifyAddress(addressWithHouseNumber);
  if (avsStatus.value.status === 'verified') {
    emitAddressSubmitted(addressWithHouseNumber);
  } else {
    displayAddressVerificationModal.value = true;
  }
};

const closeAvsModal = () => {
  displayAddressVerificationModal.value = false;
};

const tooltipTriggerUtilities = [
  'bg-black',
  'cursor-pointer',
  'h-3.5',
  'md:h-4',
  'rounded-full',
  'text-tiny',
  'text-white',
  'w-3.5',
  'md:w-4',
];

const addressFormInputAttributes = computed<Record<string, InputAttributes>>(() => ({
  fullName: {
    autocomplete: 'name',
    name: `fullName-${props.uniqueIdSuffix}`,
    placeholder: 'Full Name*',
    type: 'text',
  },
  company: {
    name: `company-${props.uniqueIdSuffix}`,
    placeholder: 'Company (optional)',
    type: 'text',
  },
  country: {
    name: `country-${props.uniqueIdSuffix}`,
    placeholder: 'Country*',
    type: 'text',
  },
  street1: {
    autocomplete: address1ElementFocused.value ? 'off' : 'address-line1',
    name: `streetName-${props.uniqueIdSuffix}`,
    placeholder: 'Address 1*',
    type: 'text',
  },
  street2: {
    name: `street2-${props.uniqueIdSuffix}`,
    placeholder: 'Address 2',
    type: 'text',
  },
  postalCode: {
    name: `postalCode-${props.uniqueIdSuffix}`,
    placeholder: 'ZIP code*',
    type: 'text',
  },
  city: {
    name: `city-${props.uniqueIdSuffix}`,
    placeholder: 'City*',
    type: 'text',
  },
  state: {
    name: `state-${props.uniqueIdSuffix}`,
    placeholder: 'State*',
    type: 'text',
  },
  phone: {
    autocomplete: 'tel',
    name: `phone-${props.uniqueIdSuffix}`,
    placeholder: 'Phone*',
    type: 'phone',
  },
}));

const { errorMessages, validatorRegex, validatorFailed } = formUtils();
const schema = computed(() => {
  const rules: AddressValidations = {
    city: (value) => required(value) || errorMessages.city.required,
    country: 'required',
    name: (value) => required(value) || errorMessages.name.required,
    phone: (value) => {
      if (!required(value)) return errorMessages.phone.required;
      if (!regex(value, [validatorRegex.phone])) return errorMessages.phone.regex;
      if (!min(value, [10])) return errorMessages.phone.min.replace('{length}', '10');
      return true;
    },
    street1: (value) => required(value) || errorMessages.street1.required,
  };

  if (addressFieldVariationsForCountry.value.postalCode) {
    rules.postalCode = (value) => {
      const { postalCode: validators } = validationRulesForCountry.value;

      const { postalCodeName: localizedField } = addressFieldVariationsForCountry.value;
      const messages = errorMessages[localizedField as keyof typeof errorMessages];
      if ('required' in validators! && 'required' in messages && !required(value)) {
        return messages.required;
      }
      const { regex: localizedRegex } = validators ?? {};
      if (localizedRegex && 'regex' in messages && !regex(value, [localizedRegex as RegExp])) {
        return messages.regex;
      }
      return true;
    };
  }

  if (addressFieldVariationsForCountry.value.state) {
    rules.state = (value) => {
      const { stateName: localizedField } = addressFieldVariationsForCountry.value;
      const messages = errorMessages[localizedField as keyof typeof errorMessages];
      return (
        required(value) ||
        ('required' in messages && messages.required) ||
        errorMessages.genericRequire.required
      );
    };
  }

  return rules;
});

const tooltip = (source: string) => emit('tooltip', source);

async function selectAddress(suggestion: ExpandableAddressSuggestion) {
  await selectAddressSuggestion(suggestion);
  emit('selected-address-suggestion');
}

function cancelAddressSuggestions() {
  declineAddressSuggestions();
  emit('cancelled-address-suggestions');
}

const isSavedDefaultAddress = address.value.default;
const infoIconFilled = nutshell['img/info-icon-filled.svg'];
</script>

<template>
  <div>
    <form data-test="address-step" @submit.prevent="submitAddress">
      <fieldset>
        <legend class="sr-only">Address form</legend>
        <slot name="guestEmailInput" />

        <template v-if="showAddressForm">
          <slot name="shippingAddressLabel" />
          <FormInput
            :class="{ 'mt-3': $slots.shippingAddressLabel }"
            v-model="address.name"
            :inputAttributes="addressFormInputAttributes.fullName"
            showLabel
            :validator="schema.name"
          />
          <FormInput
            v-if="!hideCompanyField"
            class="mt-6 md:mt-8"
            v-model="address.company"
            :inputAttributes="addressFormInputAttributes.company"
            showLabel
          />
          <Field
            v-model="address.country"
            name="country"
            :rules="schema.country"
            v-slot="{ field, meta }"
          >
            <div class="relative w-full mt-6 md:mt-8">
              <Select
                v-bind="field"
                :options="countryCodes"
                :id="`country-${uniqueIdSuffix}`"
                :invalid="validatorFailed(meta)"
                placeholder="country"
                aria-label="country"
                autocomplete="country"
                data-test="address-country"
              />
              <FloatingLabel
                :for="`country-${uniqueIdSuffix}`"
                data-test="address-country-label"
                :invalid="validatorFailed(meta)"
              >
                Country*
              </FloatingLabel>
            </div>
          </Field>
          <AddressSuggestions
            class="relative w-full mt-6 md:mt-8"
            :showSuggestions
            :suggestions="addressSuggestions"
            @cancel="cancelAddressSuggestions()"
            @select-address="selectAddress($event)"
          >
            <FormInput
              v-model="address.street1"
              :inputAttributes="addressFormInputAttributes.street1"
              showLabel
              :validator="schema.street1"
              @blur="onBlurAddress1Input"
              @focus="address1ElementFocused = true"
            />
          </AddressSuggestions>
          <div class="relative w-full mt-6 md:mt-8">
            <FormInput
              v-model="address.street2"
              :inputAttributes="addressFormInputAttributes.street2"
              showLabel
            />
            <div v-if="!hidePopper" class="absolute right-0 flex">
              <span class="self-center inline-block text-xs font-normal">
                Shipping to APO/FPO?
              </span>
              <div class="tiny-note popover-container">
                <PopperWrapper
                  arrow
                  class="legacy-max-width"
                  offsetDistance="10"
                  placement="bottom"
                  @open:popper="tooltip('Shipping')"
                >
                  <template v-slot:content>
                    <div
                      class="p-3 text-xs text-neutral-500"
                      data-test="shipping-address-popper"
                      aria-live="polite"
                    >
                      Great! Just enter <strong>APO</strong> or <strong>FPO</strong> in the City
                      field and select your designator <em class="italic">(eg; AP)</em> in the state
                      drop-down box.
                    </div>
                  </template>
                  <img
                    role="button"
                    :aria-describedby="`shipping-address-popper-locator-${uniqueIdSuffix}`"
                    alt="shipping address details related to APO/FPO"
                    data-test="shipping-address-popper"
                    class="ml-2 transform -translate-y-0.5"
                    :class="tooltipTriggerUtilities"
                    :src="infoIconFilled"
                    tabindex="0"
                  />
                </PopperWrapper>
                <span :id="`shipping-address-popper-locator-${uniqueIdSuffix}`" class="sr-only">
                  shipping address details related to APO/FPO are shown in a tooltip
                </span>
              </div>
            </div>
          </div>
          <div class="flex flex-col w-full mt-6 lg:flex-row lg:items-center md:mt-8">
            <FormInput
              v-if="addressFieldVariationsForCountry.postalCode"
              class="flex-none w-1/2 md:w-40"
              v-model="address.postalCode"
              :inputAttributes="addressFormInputAttributes.postalCode"
              showLabel
              :validator="schema.postalCode"
            />
            <div class="flex w-full mt-6 md:mt-8 lg:mt-0">
              <FormInput
                class="w-4/6 mr-2 lg:mx-2"
                v-model="address.city"
                :inputAttributes="addressFormInputAttributes.city"
                showLabel
                :validator="schema.city"
              />
              <Field
                v-if="addressFieldVariationsForCountry.state"
                v-model="address.state"
                name="state"
                :rules="schema.state"
                v-slot="{ field, meta }"
              >
                <div class="relative w-2/6">
                  <Select
                    v-bind="field"
                    :options="statesOrProvinces"
                    :id="`state-${uniqueIdSuffix}`"
                    :invalid="validatorFailed(meta)"
                    placeholder="state"
                    aria-label="state"
                    autocomplete="address-level1"
                    required
                    data-test="address-state"
                  />
                  <FloatingLabel
                    :for="`state-${uniqueIdSuffix}`"
                    data-test="address-state-label"
                    :invalid="validatorFailed(meta)"
                  >
                    {{ addressFieldVariationsForCountry.state }}*
                  </FloatingLabel>
                </div>
              </Field>
            </div>
          </div>
          <div class="flex items-center mt-6 md:mt-8">
            <FormInput
              class="flex-none w-2/3 md:w-7/12"
              v-model="address.phone"
              :inputAttributes="addressFormInputAttributes.phone"
              showLabel
              :validator="schema.phone"
            />
            <div v-if="!hidePopper" class="tiny-note popover-container">
              <PopperWrapper
                arrow
                class="legacy-max-width"
                offsetDistance="10"
                placement="bottom"
                @open:popper="tooltip('Phone')"
              >
                <template v-slot:content>
                  <div
                    class="p-3 text-xs text-neutral-500"
                    data-test="phone-number-popper"
                    aria-live="polite"
                  >
                    In case we’ll need to reach out about your order
                  </div>
                </template>
                <img
                  role="button"
                  :aria-describedby="`phone-number-popper-locator-${uniqueIdSuffix}`"
                  alt="phone number information"
                  class="ml-2 transform -translate-y-0.5"
                  :class="tooltipTriggerUtilities"
                  :src="infoIconFilled"
                  tabindex="0"
                />
              </PopperWrapper>
              <span :id="`phone-number-popper-locator-${uniqueIdSuffix}`" class="sr-only">
                phone number information is shown in a tooltip
              </span>
            </div>
          </div>
          <DefaultAddressPopper
            v-if="showMakeDefault && addresses.length && !isSavedDefaultAddress"
            location="checkout"
            v-slot="popperProps"
          >
            <Checkbox
              v-model="address.default"
              class="mt-4 text-base font-bold pt-0.5"
              @click="popperProps?.dismiss()"
            >
              Make this my default address
            </Checkbox>
          </DefaultAddressPopper>
          <Checkbox
            v-else-if="showMakeDefault && addresses.length"
            v-model="address.default"
            class="mt-4 text-base font-bold pt-0.5"
            :disabled="isSavedDefaultAddress"
          >
            Make this my default address
          </Checkbox>
        </template>
        <slot name="giftRecipient" />
        <div
          v-if="showB2BCheckbox"
          class="flex items-center mt-4"
          data-test="multi-ship-guest-subscribe-prompt"
        >
          <Checkbox
            v-model="isB2B"
            class="m-0 text-base font-bold pt-0.5"
            data-test="subscribe-B2B-checkbox"
          >
            Buying for business?
          </Checkbox>
          <div v-if="!hidePopper" class="tiny-note popover-container">
            <PopperWrapper
              arrow
              class="legacy-max-width"
              offsetDistance="10"
              placement="bottom"
              @open:popper="tooltip('Business')"
            >
              <template v-slot:content>
                <div
                  class="p-3 text-xs text-neutral-500"
                  data-test="business-account-popper"
                  aria-live="polite"
                >
                  As an eligible business, you’ll have access to exclusive offers, dedicated account
                  support, payment terms, freight delivery & more
                </div>
              </template>
              <img
                role="button"
                :aria-describedby="`businesss-account-popper-locator-${uniqueIdSuffix}`"
                alt="business account information"
                class="ml-2 transform -translate-y-0.5"
                :class="tooltipTriggerUtilities"
                :src="infoIconFilled"
                tabindex="0"
              />
            </PopperWrapper>
            <span
              :id="`businesss-account-popper-locator-${uniqueIdSuffix}`"
              class="sr-only"
              role="alert"
              aria-live="polite"
            >
              business account information is shown in a tooltip
            </span>
          </div>
        </div>
        <slot name="saveToAddressBook" />
        <slot name="formControls" v-if="includeFormControls" v-bind="{ meta: formMeta }">
          <ThemedButton
            type="submit"
            class="mt-7"
            theme="gray"
            :disabled="validatorFailed(formMeta)"
            :isLoading="isButtonLoading"
            data-test="address-submit"
          >
            <slot name="submitText">Continue</slot>
          </ThemedButton>
        </slot>
      </fieldset>
    </form>
    <slot name="avsModal">
      <AvsModal
        :addressProvided="address"
        :avsStatus
        :displayAddressVerificationModal
        @use-suggested-address="useSuggestedAddress"
        @use-entered-address="useEnteredAddress"
        @confirm-unit-number="confirmUnitNumber"
        @confirm-house-number="confirmHouseNumber"
        @close-avs-modal="closeAvsModal"
      />
    </slot>
  </div>
</template>

<style lang="scss" scoped>
.popover-container {
  :deep(.popper__arrow) {
    margin-left: 0;
  }
}
</style>
